import {
  ChartType,
  FilteredBreakdownSegments,
  IndividualSelectedSegments,
  MATH_TYPE,
  PER_USER_AGGREGATIONS,
  PropertyType,
  ValueRepresentationType,
} from 'common/report/constants';
import {ComparisonDateRangeBookmarkFormat} from 'common/query-builder/query-block/time-comparison-menu/types';
import {BaseDate, DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {Behavior, CustomProperty, Segmentation} from 'common/types/reports/query';
import {FlatSortConfig, SortBy, SortConfig, SortOrder} from 'common/report/util/sorting/types';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';
import {BaseQueryErrorType, BaseQueryResultsType, ForecastValue} from 'common/report/types';
import {Bucket} from 'common/widgets/bucket-pane/types';
import {MultiAttribution} from 'common/widgets/multi-attribution-menu/types';
import {BehaviorType} from 'common/query-builder/query-block/constants';

export interface FlatSegment<ValueT = number> {
  key: string;
  path: string[];
  displayName: string;
  displayNamePath?: string[];
  // object key is an ISO-8601 date string for time data
  // or a single arbitrary leaf key for series data e.g. `all`, `value`, `null`
  value: Record<string, ValueT>;
}

export type AbsoluteDateRange = {
  fromDate: string;
  toDate: string;
};

export enum TypeOfComparison {
  Time = `time_comparison`,
  Lift = `lift_comparison`,
}

export enum ChartPlotStyle {
  Standard = `standard`,
  Stacked = `stacked`,
}

export enum AnalysisType {
  Linear = `linear`,
  Logarithmic = `logarithmic`,
  Rolling = `rolling`,
  Cumulative = `cumulative`,
}

export interface DisplayOptions {
  chartType: InsightsChartType;
  plotStyle: ChartPlotStyle;
  analysis: AnalysisType;
  value: ValueRepresentationType;
  rollingWindowSize?: number;
  timeUnit?: TimeUnit;
}

export type InsightsChartType = ChartType.Bar | ChartType.Line | ChartType.Pie | ChartType.Table | ChartType.Metric;

export type FlatOrColumnSortConfig = FlatSortConfig | SortConfig;

/** Subset of SortByColumnsConfig:
 * 1) no valueField (used for lift in bar charts, I think)
 * 2) colSortAttrs can only be FlatLabelSortConfig, not FlatValueSortConfig */
export type OldTableSortByColumn = {
  sortBy: SortBy.Column;
  colSortAttrs: Array<{sortBy: SortBy.Label; sortOrder: SortOrder}>;
};

/** Same as SortByValueConfig except:
 * 1) sortColumn (always 'sum') instead of valueField
 * 2) colSortAttrs can only be FlatLabelSortConfig, not FlatValueSortConfig
 *
 * Must convert this for use with data-table
 */
export type OldTableSortByValue = {
  sortBy: SortBy.Value;
  sortOrder: SortOrder;
  sortColumn: 'sum';
  colSortAttrs: Array<{sortBy: SortBy.Label; sortOrder: SortOrder}>;
};

export type OldTableSortConfig = OldTableSortByColumn | OldTableSortByValue;

export type InsightsBookmarkSortConfig = {
  [ChartType.Bar]: SortConfig;
  [ChartType.Table]: SortConfig;
  // In the bookmark, line chart can either have the old FlatSortConfig or the new SortConfig format
  [ChartType.Line]: FlatOrColumnSortConfig;
  [ChartType.Metric]: SortConfig;
  [ChartType.Pie]: SortConfig;
};

export type InsightsSortConfig = InsightsBookmarkSortConfig & {
  [ChartType.Line]: SortConfig;
};

export enum InsightsResourceType {
  Events = `events`,
  People = `people`,
  Cohorts = `cohorts`,

  // @deprecated the "all" resource type is no longer valid for Insights (for more info, search for "test_patch_for_resource_type_all_bug" in query-api code)
  All = `all`,
}

// New format which show clause will transform into
export interface BehaviorMetric {
  /* behavior */
  behavior: {
    id?: string; // not applicable now
    type: BehaviorType;
    name?: string;
    unsavedId?: string;
    filter?: TODO;
    resourceType: InsightsResourceType;
    profileType?: string;
    formulaLabel?: string;
    labelSuffix?: string;
    labelPrefix?: string;
    dataGroupId?: string;
    dataset?: string;
    datasetId?: string;
    projectId?: number;
    customBucket?: Bucket;
    multiAttribution?: MultiAttribution;
    // TODO(CC): when we support event selections within behavior UI, create a new field i.e. "entries" array
    // and put all the selected events to entries. For now, we only support single event selection for each metric,
    // there is only one value for each metric
    value: {
      name: string | string[];
      renamed?: string;
      custom?: boolean;
      id?: number;
      resourceType?: InsightsResourceType;
    };
  };
  /* measurement */
  measurement: {
    math: MATH_TYPE;
    perUserAggregation?: MATH_TYPE;
    property?: {
      type: PropertyType;
      name: string | string[];
      resourceType: InsightsResourceType;
      unit?: TimeUnit;
    };
    rolling?: {
      rollingWindowSize: number;
    };
    cumulative?: boolean;
  };
}

export interface ShowClause {
  /* measurement */
  math: MATH_TYPE;
  perUserAggregation?: PER_USER_AGGREGATIONS;
  property?: {
    type: PropertyType;
    name: string | string[];
    resourceType: InsightsResourceType;
    unit?: TimeUnit;
  };
  rolling?: {
    rollingWindowSize: number;
  };
  cumulative?: boolean;
  /* behavior */
  resourceType: InsightsResourceType;
  profileType?: string;
  value: {
    name: string | string[];
    renamed?: string;
    custom?: boolean;
    id?: number;
    resourceType?: InsightsResourceType;
  };
  filter?: TODO;
  formulaLabel?: string;
  labelSuffix?: string;
  labelPrefix?: string;
  dataGroupId?: string;
  dataset?: string;
  datasetId?: string;
  projectId?: number;
  customBucket?: Bucket;
  multiAttribution?: MultiAttribution;
}

export interface GroupClause {
  customBucket?: Bucket;
  customGroups?: TODO;
  propertyType?: PropertyType;
  resourceType: InsightsResourceType;
  typeCast?: PropertyType;
  unit?: TimeUnit;
  dataset?: string;
  datasetId?: string;
  projectId?: number;
  dataGroupId?: string;
  tempDataGroupId?: string;
  joinPropertyType?: PropertyType;
  customPropertyId?: number | string;
  customProperty?: CustomProperty;
  value: string;
  propertyObjectKey?: string | string[];
  listItemGroup?: GroupClause;
  behavior?: Behavior;
}

export interface FormulaClause {
  value: string;
  label?: string;
}

export interface TimeClause {
  unit: TimeUnit;
  value?: number | string | [BaseDate] | [BaseDate, BaseDate];
  from_date?: BaseDate;
  to_date?: BaseDate;
  rolling?: boolean;
  window?: {
    unit: TimeUnit;
    value: number;
  };
  dateRangeType?: DateRangeType;
  dataset?: string;
  time_aggregation_unit?: TimeUnit;
  as_of_timestamp?: number;
  time_comparison?: TODO;
  lift_comparison?: TODO;
}

export interface InsightsBookmarkSections {
  show: ShowClause[];
  cohorts?: TODO[];
  group_by?: Segmentation[];
  group?: GroupClause[];
  filter?: TODO[];
  formula?: FormulaClause[];
  time: TimeClause[];
  globalDataGroupId?: string;
}

export interface InsightsBookmarkParams {
  columnWidths: {
    // percentages (bar chart is fixed width)
    bar: {[columnIdx: string]: number};
  };
  displayOptions: DisplayOptions;
  name?: string;
  sections: InsightsBookmarkSections;
  sorting: Partial<InsightsBookmarkSortConfig>;
  timeComparison?: ComparisonDateRangeBookmarkFormat;
  liftComparison?: LiftComparison;
  legend?: InsightsLegend;
}
export type InsightsChartParams = Pick<InsightsBookmarkParams, 'sections' | 'displayOptions' | 'liftComparison'>;

export type InsightsSeriesTree<ValueT = number> = {[k: string]: ValueT} | {[k: string]: InsightsSeriesTree};

interface DateRange {
  // sic; this is NOT BookmarkDateRange or a similar bookmark-related type
  from_date: string;
  to_date: string;
}

export interface MetricMetadata {
  metric_key?: string;
  sub_queries?: Array<{metric_key: string}>;
  /*
    Augmented on the client-side by queries/insights based on query limits and segments in the result
  */
  apiResultsTruncated?: boolean;
}

export interface ReportSectionsMetadata {
  group?: Array<{bookmark: GroupClause; numeric_buckets?: Bucket}>;
  show?: MetricMetadata[];
  formula?: MetricMetadata[];
}

export interface SegmentationQueryLimits {
  limit?: number;
}

export interface InsightsQueryError extends BaseQueryErrorType {
  formula_errors?: Array<{index: number; message: string}>;
}

export interface InsightsQueryResults extends BaseQueryResultsType {
  date_range: DateRange;
  headers: string[];
  meta: {
    min_sampling_factor: number;
    report_sections: ReportSectionsMetadata;
    /*
    Augmented on the client-side to carry the limits used on the query that generated these results.
    When limits affect the results, we need to tell the user where the cutoff is.
    */
    queryLimits?: SegmentationQueryLimits;
    apiResultsTruncated?: boolean;
    liftComparison?: any;
    time_period_settings?: {quarter_start_month: number};
  };
  series: InsightsSeriesTree;
  forecast_comparison?: {
    series: InsightsSeriesTree<ForecastValue>;

    // these properties are intended for manual debugging and shouldn't be referenced in non-experimental code, they will likely be removed after GA
    date_range: {
      historical_from_date: string;
      historical_to_date: string;
    };
    historical_series: InsightsSeriesTree;
  };
  time_comparison?: {
    date_range: DateRange;
    series: InsightsSeriesTree;
  };
  lift_comparison?: {
    series: InsightsSeriesTree;
  };
}

export interface AggregatedSeriesValue {
  // list of all possible paths to reach the segment in this series
  paths: string[][];

  // sum of all of this segments values in this series
  total: number;
}

export interface AggregatedSeries {
  [label: string]: AggregatedSeriesValue;
}

export interface InsightsDeprecatedLegendUrlData {
  idxSelections?: number[] | number[][];
}

export interface InsightsLegend {
  // used for flat data, entire selected segments
  individualSelectedSegments?: IndividualSelectedSegments;
  // used for nested (bar chart) data, filtered segments at each level
  filteredBreakdownSegments?: FilteredBreakdownSegments;
}
